import _ from 'lodash';

import {
  isReference,
  Reference
} from '@apollo/client';
import * as types from 'generated/graphql';

import { ReadFieldFunction } from '@apollo/client/cache/core/types/common';
import { reactiveVariables } from 'modules/common/layer00';
import { filterInstallationsVar } from 'modules/common/layer00/reactiveVariables';
import { ITypeSpecificPolicy } from 'modules/common/layer01/apolloClient/cache/typePolicies/shared';
import { dateTimeFieldPolicy } from './fieldPolicies';



export const Project: ITypeSpecificPolicy<types.Project> = {
  fields: {

    dueTasks: {
      read: (existing, { readField }) => {
        const tasks = readField<types.Task[]>('tasks');

        return tasks;
      }
    },

    overdueTasks: {
      read: (existing, { readField }) => {
        const tasks = readField<unknown[]>('tasks');

        const overdueTasks = tasks?.filter((task) => {
          const isRef = isReference(task);
          if (isRef) {
            const fieldValue = readField<boolean>('isOverdue', task);
            return fieldValue;
          }

          return false;
        }) ?? [];

        return overdueTasks;
      }
    },

    newTasks: {
      read: (existing, { readField }) => {
        const tasks = readField<types.Task[]>('tasks');
        return tasks;
      }
    },

    completedTasks: {
      read: (existing, { readField }) => {
        const tasks = readField<unknown[]>('tasks');

        const completedTasks = tasks?.filter((task) => {
          const isRef = isReference(task);
          if (isRef) {
            const fieldValue = readField<boolean>('isComplete', task);
            return fieldValue;
          }

          return false;
        }) ?? [];

        return completedTasks ?? [];
      }
    },
    rooms: {
      read: (existing, { readField }) => {

        const installationRefs = readField<Reference[]>('installations') ?? [];

        const installationRoomNames = installationRefs.map((node) => {
          const roomName = readField<string>('roomName', node);
          return roomName;
        });
        const unique = _.uniq(installationRoomNames).map((node) => ({
          name: node
        }));

        return _.sortBy(unique, (node) => node.name) ?? undefined;
      }
    },
    installations: {

      read: (existing, { readField }) => {

        const installationFilter = filterInstallationsVar();

        if (!existing || existing.length === 0) {
          return existing;
        }

        const installationArray: Reference[] = [];

        if (!_.isNull(installationFilter.freeTextSearch)) {
          const matching = __findInstallationByName(existing, readField, installationFilter.freeTextSearch);
          installationArray.push(...matching);
          return installationArray;
        }

        if (installationFilter.isComplete) {
          __getCompleteInstallations(existing, readField, installationArray);
        }

        if (installationFilter.isOverDue) {

          __getOverdueInstallations(existing, readField, installationArray);
        }

        if (installationFilter.isUpcoming) {
          const dueSoonInstallations = __getInstallationsDueSoon(existing, readField);
          installationArray.push(...dueSoonInstallations);
        }

        if (installationFilter.isRemaining) {
          const remainingInstallations = existing.filter((node: Reference) => {
            const isComplete = readField<Reference[]>('isComplete', node);
            return !isComplete;
          });

          installationArray.push(...remainingInstallations) ?? [];
        }

        // filtering behavior below
        // this is different from above

        let uniqueInstalls = _.uniq(installationArray);

        if (installationFilter.installationTypeId && installationFilter.installationTypeId !== '') {
          uniqueInstalls = __findInstallationsWithType(uniqueInstalls, readField, installationFilter);
        }

        if (installationFilter.roomName && installationFilter.roomName !== '') {
          uniqueInstalls = __findInstallationByRoomName(uniqueInstalls, readField, installationFilter);
        }

        if (installationFilter.areaId && installationFilter.areaId !== '') {
          uniqueInstalls = uniqueInstalls.filter((node) => {
            const area = readField<Reference>('area', node);
            if (!isReference(area)) {
              return false;
            }
            const id = readField<string>('id', area);
            return id === installationFilter.areaId;
          });
        }

        if (installationFilter.milestoneId && installationFilter.milestoneId !== '') {
          uniqueInstalls = uniqueInstalls.filter((node) => {
            const milestone = readField<Reference>('milestone', node);
            if (!milestone) {
              return false;
            }
            const id = readField<string>('id', milestone);
            return id === installationFilter.milestoneId;
          });
        }

        return uniqueInstalls;
      }

    },

    installationsWithAllDecisionsComplete: {
      read: (existing, { readField }) => {

        const installations = readField<Reference[]>('installations') ?? [];

        const completedInstallations = installations.filter((node) => {
          const isComplete = readField('isComplete', node);
          return isComplete;
        });

        return completedInstallations ?? [];
      }
    },
    installationsWithDecisionsRemaining: {
      read: (existing, { readField }) => {

        const installations = readField<Reference[]>('installations') ?? [];

        const remainingInstallations = installations.filter((node) => {
          const isComplete = readField<Reference[]>('isComplete', node);
          return !isComplete;
        });

        return remainingInstallations ?? [];
      }
    },
    installationsWithOverDueDecisions: {
      read: (existing, { readField }) => {

        const installations = readField<Reference[]>('installations') ?? [];


        const overDueInstallations = installations.filter((node) => {
          const isOverDue = readField<boolean>('isOverDue', node);
          return isOverDue;
        });
        return overDueInstallations ?? [];
      }
    },
    installationsWithUpcomingDecisions: {
      read: (existing, { readField }) => {

        const installations = readField<Reference[]>('installations') ?? [];

        const dueSoonInstallations = installations.filter((node) => {
          const isDueSoon = readField<boolean>('isDueSoon', node);
          return isDueSoon;
        });

        return dueSoonInstallations ?? [];
      }
    },
    documents: {
      merge: (existing, incoming, { }) => {
        if (existing) {
          return (_.uniqBy([
            ...existing,
            ...incoming
          ], (node) => node.__ref));
        }
        return incoming;
      },
      read: (existing, { readField, isReference }) => {
        const documentsFilter = reactiveVariables.filterDocumentsVar();
        if (!existing) {
          return existing;
        }
        const existingDocs = existing ?? [];
        if (documentsFilter.folderId) {
          const filteredDocs = existingDocs.filter((node: Reference) => {
            const folderRef = readField<Reference>('folder', node);
            if (isReference(folderRef)) {
              const folderId = readField('id', folderRef);
              return folderId === documentsFilter.folderId;
            }
            return false;
          });
          return filteredDocs;
        } else {
          const filteredDocs = existingDocs.filter((node: Reference) => {
            const folderRef = readField<Reference>('folder', node);
            return _.isNull(folderRef);
          });
          return filteredDocs;
        }
      }
    },
    endDate: dateTimeFieldPolicy(),
    estimatedStartDate: dateTimeFieldPolicy(),
    estimatedCompletionDate: dateTimeFieldPolicy(),
    startDate: dateTimeFieldPolicy()
  }
};

function __findInstallationsWithType(uniqueInstalls: Reference[], readField: ReadFieldFunction, installationFilter: reactiveVariables.IFilterInstallationsVar) {
  uniqueInstalls = uniqueInstalls.filter((node) => {
    const installationType = readField<Reference>('installationType', node);
    const id = readField<string>('id', installationType);

    if (installationFilter.installationTypeId === id) {
      return true;
    }
    return false;
  });
  return uniqueInstalls;
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function __findInstallationByRoomName(existing: any, readField: ReadFieldFunction, installationFilter: reactiveVariables.IFilterInstallationsVar) {
  const matching = existing.filter((node: Reference) => {
    const roomName = readField('roomName', node);
    const isRoomNameMatch = roomName === installationFilter.roomName;
    return isRoomNameMatch;
  });
  return matching;
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function __getOverdueInstallations(existing: any, readField: ReadFieldFunction, installationArray: Reference[]) {
  const overDueInstallations = existing.filter((node: Reference) => {
    const isOverDue = readField<boolean>('isOverDue', node);
    const isComplete = readField('isComplete', node);
    return !isComplete && isOverDue;
  });

  installationArray.push(...overDueInstallations);
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function __getCompleteInstallations(existing: any, readField: ReadFieldFunction, installationArray: any[]) {
  const completedInstallations = existing.filter((node: Reference) => {
    const isComplete = readField('isComplete', node);
    return isComplete;
  });

  installationArray.push(...completedInstallations);
}

function __getInstallationsDueSoon(existing: Reference[], readField: ReadFieldFunction) {
  return existing.filter((node: Reference) => {
    const isDueSoon = readField<boolean>('isDueSoon', node);
    const isComplete = readField('isComplete', node);
    return !isComplete && isDueSoon;
  });
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function __findInstallationByName(existing: any, readField: ReadFieldFunction, searchText: string) {
  const matchingInstallation = existing.filter((node: Reference) => {
    const rawName = readField<string>('name', node);
    const name = _.toLower(rawName);
    return name?.includes(_.toLower(searchText)) ? true : false;
  });

  return matchingInstallation;
}

